import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Headset, SignOut, PaperPlaneTilt, Sparkle, CheckCircle, Robot,
  UserCircle, CircleNotch, ChatCircleDots, Phone, Tray, ChartBar, Clock, Smiley, SmileyMeh, SmileySad,
  BellSimple, BellSimpleSlash, CaretDown, Lightning, NotePencil, ArrowsLeftRight, Plus, Trash, PencilSimple, X, Warning,
} from "@phosphor-icons/react";

const AVAIL = {
  online: { label: "Online", color: "#10b981" },
  busy: { label: "Upptagen", color: "#f59e0b" },
  away: { label: "Borta", color: "#71717a" },
};

const STATUS_META = {
  waiting: { label: "I kö", cls: "bg-amber-100 text-amber-700" },
  active: { label: "Pågår", cls: "bg-emerald-100 text-emerald-700" },
  resolved: { label: "Löst", cls: "bg-zinc-100 text-zinc-500" },
  ai: { label: "AI", cls: "bg-blue-100 text-blue-700" },
};

const SENT = {
  positive: { label: "Positiv", Icon: Smiley, color: "#16a34a" },
  neutral: { label: "Neutral", Icon: SmileyMeh, color: "#a16207" },
  negative: { label: "Negativ", Icon: SmileySad, color: "#dc2626" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "nyss";
  if (s < 3600) return `${Math.floor(s / 60)} min sedan`;
  if (s < 86400) return `${Math.floor(s / 3600)} h sedan`;
  return `${Math.floor(s / 86400)} d sedan`;
}

function fmtClock(secs) {
  if (secs == null) return "";
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `${r}s`;
}

function waitSeconds(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

const SEV = {
  high: { label: "Hög", bg: "#fee2e2", color: "#b91c1c" },
  medium: { label: "Medel", bg: "#fef3c7", color: "#b45309" },
  low: { label: "Låg", bg: "#dcfce7", color: "#15803d" },
};
const sevStyle = (s) => { const v = SEV[s] || SEV.medium; return { background: v.bg, color: v.color }; };
const sevLabel = (s) => (SEV[s] || SEV.medium).label;
const sentColor = (s) => (s === "positive" ? "#16a34a" : s === "negative" ? "#dc2626" : "#a1a1aa");

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
        <Icon size={14} weight="fill" className="text-[#0033FF]" /> {label}
      </div>
      <div className="mt-1 font-display font-extrabold text-xl">{value}</div>
    </div>
  );
}

export default function AgentConsole() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [scope, setScope] = useState("mine");
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ queue: 0, mine: 0, online_agents: 0, sla_breached: 0 });
  const [activeId, setActiveId] = useState(null);
  const [conv, setConv] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insGenerating, setInsGenerating] = useState(false);

  // SLA: warn/breach thresholds + live ticking + breach alarm.
  const [sla, setSla] = useState({ warn_seconds: 60, breach_seconds: 180 });
  const [, setTick] = useState(0);
  const prevBreachRef = useRef(0);

  // Snabbsvar (canned responses) + internal notes + transfer.
  const [canned, setCanned] = useState([]);
  const [cannedOpen, setCannedOpen] = useState(false);
  const [manageCanned, setManageCanned] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [slaOpen, setSlaOpen] = useState(false);

  // Internal KB assistant chat (grounded in this conversation's bot knowledge base).
  const [kbMsgs, setKbMsgs] = useState([]);
  const [kbInput, setKbInput] = useState("");
  const [kbLoading, setKbLoading] = useState(false);
  const kbThreadRef = useRef(null);

  const threadRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  // Queue notifications so agents never miss a waiting customer (even on another tab).
  const [muted, setMuted] = useState(() => localStorage.getItem("senzum_agent_muted") === "1");
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const prevQueueRef = useRef(null);
  const prevMineRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(() => { localStorage.setItem("senzum_agent_muted", muted ? "1" : "0"); }, [muted]);
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Availability + concurrent-chat cap (controls auto-assignment eligibility).
  const [myStatus, setMyStatus] = useState(() => localStorage.getItem("senzum_agent_status") || "online");
  const [maxChats, setMaxChats] = useState(() => parseInt(localStorage.getItem("senzum_agent_maxchats") || "3", 10));
  const [statusOpen, setStatusOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem("senzum_agent_status", myStatus);
    localStorage.setItem("senzum_agent_maxchats", String(maxChats));
    api.post("/inbox/status", { status: myStatus, max_chats: maxChats }).catch(() => {});
  }, [myStatus, maxChats]);

  // Heartbeat: keeps this agent connected (fresh last_seen) for auto-assignment.
  useEffect(() => {
    let stopped = false;
    const beat = () => { if (!stopped) api.post("/inbox/heartbeat").catch(() => {}); };
    beat();
    const id = setInterval(beat, 15000);
    return () => { stopped = true; clearInterval(id); api.post("/inbox/offline").catch(() => {}); };
  }, []);

  const playBeep = useCallback(() => {
    if (mutedRef.current) return;
    try {
      audioCtxRef.current = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
    } catch { /* audio not available */ }
  }, []);

  const pushNotice = useCallback((title, body) => {
    toast.message(title, { description: body });
    playBeep();
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const n = new Notification(title, { body, tag: "senzum-queue" });
        n.onclick = () => { window.focus(); n.close(); };
      }
    } catch { /* ignore */ }
  }, [playBeep]);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/conversations", { params: { scope } });
      setList(data.conversations || []);
      const c = data.counts || { queue: 0, mine: 0, online_agents: 0, sla_breached: 0 };
      setCounts(c);
      if (data.sla) setSla(data.sla);
      // Notify when a chat is auto-assigned to me, or lands in the shared queue.
      if (prevMineRef.current !== null && c.mine > prevMineRef.current) {
        pushNotice("Senzum · Ny chatt tilldelad dig", "En kund har överlämnats till dig.");
      } else if (prevQueueRef.current !== null && c.queue > prevQueueRef.current) {
        pushNotice(c.queue - prevQueueRef.current > 1 ? "Senzum · Nya chattar i kön" : "Senzum · Ny chatt i kön", "En kund väntar på en agent.");
      }
      // SLA alarm: a queued chat has waited past the breach threshold.
      if ((c.sla_breached || 0) > prevBreachRef.current) {
        pushNotice("Senzum · SLA-larm ⚠️", "En kund har väntat för länge i kön.");
      }
      prevBreachRef.current = c.sla_breached || 0;
      prevQueueRef.current = c.queue;
      prevMineRef.current = c.mine;
    } catch { /* ignore */ }
  }, [scope, pushNotice]);

  const loadConv = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/inbox/conversations/${id}`);
      setConv(data);
    } catch { /* ignore */ }
  }, []);

  // Keep latest callbacks in refs so the WS effect can run once (no reconnects).
  const loadListRef = useRef(loadList); loadListRef.current = loadList;
  const loadConvRef = useRef(loadConv); loadConvRef.current = loadConv;
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);
  const [customerTyping, setCustomerTyping] = useState(false);

  // Realtime: instant inbox refresh + typing via WebSocket (polling stays as fallback).
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const token = localStorage.getItem("senzum_token");
    let stop = false, retry = null, ws = null;
    const connect = () => {
      if (stop) return;
      try { ws = new WebSocket(`${base}/api/ws/agent?token=${token}`); } catch { return; }
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === "inbox" || d.type === "new") {
            loadListRef.current?.();
            if (activeIdRef.current) loadConvRef.current?.(activeIdRef.current);
          } else if (d.type === "typing" && d.who === "customer") {
            if (!d.conv_id || d.conv_id === activeIdRef.current) setCustomerTyping(!!d.on);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { wsRef.current = null; if (!stop) retry = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { stop = true; clearTimeout(retry); try { ws && ws.close(); } catch { /* ignore */ } };
  }, []);

  const onDraftChange = (val) => {
    setDraft(val);
    const ws = wsRef.current;
    if (ws && ws.readyState === 1 && activeId) {
      try { ws.send(JSON.stringify({ type: "typing", conv_id: activeId, on: true })); } catch { /* ignore */ }
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        try { ws.send(JSON.stringify({ type: "typing", conv_id: activeId, on: false })); } catch { /* ignore */ }
      }, 1800);
    }
  };

  // poll the list (fallback — WS triggers instant refreshes)
  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 12000);
    return () => clearInterval(t);
  }, [loadList]);

  // poll the open conversation (fallback)
  useEffect(() => {
    if (!activeId) return;
    loadConv(activeId);
    const t = setInterval(() => loadConv(activeIdRef.current), 8000);
    return () => clearInterval(t);
  }, [activeId, loadConv]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [conv?.messages?.length]);

  useEffect(() => {
    if (tab === "stats") api.get("/inbox/stats").then((r) => setStats(r.data)).catch(() => {});
    if (tab === "insights" && insights === null) api.get("/inbox/insights").then((r) => setInsights(r.data)).catch(() => setInsights(false));
  }, [tab, insights]);

  const generateInsights = async () => {
    setInsGenerating(true);
    try {
      const { data } = await api.post("/inbox/insights/generate");
      setInsights(data);
      toast.success("AI-insikter uppdaterade");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte generera insikter");
    } finally { setInsGenerating(false); }
  };

  // Reflect the queue size in the browser tab so agents notice it from another tab.
  useEffect(() => {
    const base = "Senzum · Agentläge";
    document.title = counts.queue > 0 ? `(${counts.queue}) ${base}` : base;
    return () => { document.title = "Senzum AI"; };
  }, [counts.queue]);

  // 1s tick to drive live SLA/queue timers.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Load canned responses + SLA settings once.
  const loadCanned = useCallback(async () => {
    try { const { data } = await api.get("/inbox/canned"); setCanned(data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCanned(); api.get("/inbox/sla").then((r) => setSla(r.data)).catch(() => {}); }, [loadCanned]);

  const insertCanned = (text) => {
    setDraft((cur) => (cur.trim() ? `${cur.trim()} ${text}` : text));
    setCannedOpen(false);
  };

  const applyKbAsReply = (text) => {
    setDraft((cur) => (cur.trim() ? `${cur.trim()} ${text}` : text));
    toast.success("Svaret infogat i komponeringsrutan – granska och skicka");
  };

  const openTransfer = async () => {
    setTransferOpen(true);
    try { const { data } = await api.get("/inbox/agents"); setColleagues(data || []); } catch { setColleagues([]); }
  };

  const doTransfer = async (agentId) => {
    if (!activeId) return;
    try {
      const { data } = await api.post(`/inbox/conversations/${activeId}/transfer`, { agent_id: agentId });
      toast.success(`Överlämnad till ${data.assigned_agent_name}`);
      setTransferOpen(false);
      await loadList();
      await loadConv(activeId);
    } catch { toast.error("Kunde inte överlämna chatten"); }
  };

  const saveSla = async (warn, breach) => {
    try {
      const { data } = await api.put("/inbox/sla", { warn_seconds: warn, breach_seconds: breach });
      setSla(data);
      toast.success("SLA-inställningar sparade");
    } catch { toast.error("Kunde inte spara SLA-inställningar"); }
  };

  const sendKb = async () => {
    const q = kbInput.trim();
    if (!q || !activeId || kbLoading) return;
    const history = kbMsgs.map((m) => ({ role: m.role, content: m.content }));
    setKbMsgs((cur) => [...cur, { role: "user", content: q }]);
    setKbInput("");
    setKbLoading(true);
    try {
      const { data } = await api.post(`/inbox/conversations/${activeId}/kb-chat`, { message: q, history });
      setKbMsgs((cur) => [...cur, { role: "assistant", content: data.answer || "—", sources: data.sources || [] }]);
    } catch {
      setKbMsgs((cur) => [...cur, { role: "assistant", content: "Kunde inte svara just nu. Försök igen.", error: true }]);
    } finally { setKbLoading(false); }
  };

  useEffect(() => {
    if (kbThreadRef.current) kbThreadRef.current.scrollTop = kbThreadRef.current.scrollHeight;
  }, [kbMsgs.length, kbLoading]);

  const openConv = (id) => { setActiveId(id); setConv(null); setDraft(""); setCustomerTyping(false); setKbMsgs([]); setKbInput(""); };

  const act = async (action) => {
    if (!activeId) return;
    try {
      await api.post(`/inbox/conversations/${activeId}/${action}`);
      const labels = { claim: "Du hanterar nu konversationen", release: "Lämnad tillbaka till AI", resolve: "Markerad som löst" };
      toast.success(labels[action] || "Klart");
      await loadConv(activeId);
      await loadList();
    } catch { toast.error("Något gick fel"); }
  };

  const sendReply = async () => {
    const text = draft.trim();
    if (!text || !activeId) return;
    setSending(true);
    try {
      await api.post(`/inbox/conversations/${activeId}/reply`, { content: text });
      setDraft("");
      await loadConv(activeId);
      await loadList();
    } catch { toast.error("Kunde inte skicka"); }
    finally { setSending(false); }
  };

  const suggest = async () => {
    if (!activeId) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/inbox/conversations/${activeId}/suggest`);
      const s = data.suggestion || "";
      setDraft((cur) => (cur.trim() ? `${cur.trim()} ${s}` : s));
      toast.success(draft.trim() ? "AI-förslag tillagt – granska och skicka" : "AI-förslag inlagt – granska och skicka");
    } catch { toast.error("Kunde inte generera förslag"); }
    finally { setSuggesting(false); }
  };

  const ChannelIcon = ({ ch }) => (ch === "voice" ? <Phone size={13} weight="fill" /> : <ChatCircleDots size={13} weight="fill" />);

  return (
    <div className="h-screen flex flex-col bg-zinc-100" data-testid="agent-console">
      {/* top bar */}
      <header className="h-14 shrink-0 bg-zinc-950 text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-[#0033FF] flex items-center justify-center"><ChatCircleDots size={18} weight="fill" /></div>
          <div className="font-display font-bold leading-tight">Senzum <span className="text-[#3D7BFF]">Chatt</span></div>
          <div className="ml-4 flex items-center gap-1 bg-white/5 rounded-lg p-1">
            <button data-testid="tab-inbox" onClick={() => setTab("inbox")} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "inbox" ? "bg-white text-zinc-900" : "text-zinc-300 hover:text-white"}`}>
              <Tray size={15} weight="fill" /> Inkorg
            </button>
            <button data-testid="tab-stats" onClick={() => setTab("stats")} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "stats" ? "bg-white text-zinc-900" : "text-zinc-300 hover:text-white"}`}>
              <ChartBar size={15} weight="fill" /> Min statistik
            </button>
            <button data-testid="tab-insights" onClick={() => setTab("insights")} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "insights" ? "bg-white text-zinc-900" : "text-zinc-300 hover:text-white"}`}>
              <Sparkle size={15} weight="fill" /> AI-insikter
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {/* availability status */}
          <div className="relative">
            <button data-testid="status-toggle" onClick={() => setStatusOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-zinc-200">
              <span className="h-2 w-2 rounded-full inline-block" style={{ background: AVAIL[myStatus].color }} />
              {AVAIL[myStatus].label}
              <CaretDown size={12} weight="bold" />
            </button>
            {statusOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-40 bg-white rounded-lg shadow-lg border border-zinc-200 py-1" data-testid="status-menu">
                  {Object.entries(AVAIL).map(([k, v]) => (
                    <button key={k} data-testid={`status-${k}`} onClick={() => { setMyStatus(k); setStatusOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50">
                      <span className="h-2 w-2 rounded-full inline-block" style={{ background: v.color }} /> {v.label}
                      {myStatus === k && <CheckCircle size={14} weight="fill" className="ml-auto text-[#0033FF]" />}
                    </button>
                  ))}
                  <div className="border-t border-zinc-100 mt-1 pt-2 px-3 pb-1">
                    <div className="text-[11px] text-zinc-400 mb-1">Max samtidiga chattar</div>
                    <div className="flex items-center gap-2">
                      <button data-testid="max-dec" onClick={() => setMaxChats((m) => Math.max(1, m - 1))}
                        className="h-6 w-6 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-100 font-bold">−</button>
                      <span data-testid="max-chats" className="font-bold text-zinc-800 w-6 text-center">{maxChats}</span>
                      <button data-testid="max-inc" onClick={() => setMaxChats((m) => Math.min(20, m + 1))}
                        className="h-6 w-6 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-100 font-bold">+</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <span data-testid="online-agents" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 bg-emerald-500/10 rounded-full px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" /> {counts.online_agents || 1} online
          </span>
          <button data-testid="toggle-sound" onClick={() => setMuted((m) => !m)}
            title={muted ? "Ljud av – klicka för att slå på" : "Ljud på – klicka för att stänga av"}
            className={`relative flex items-center justify-center h-8 w-8 rounded-lg transition-colors ${muted ? "text-zinc-500 hover:text-zinc-300" : "text-[#0033FF] bg-white/10 hover:bg-white/20"}`}>
            {muted ? <BellSimpleSlash size={18} weight="fill" /> : <BellSimple size={18} weight="fill" />}
            {counts.queue > 0 && <span data-testid="queue-badge" className="absolute -top-1 -right-1 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{counts.queue}</span>}
          </button>
          <span className="text-zinc-300">{user?.name || user?.email}</span>
          <button onClick={logout} data-testid="agent-logout" className="text-zinc-400 hover:text-white" title="Logga ut"><SignOut size={18} /></button>
        </div>
      </header>

      {tab === "stats" ? (
        <div className="flex-1 overflow-y-auto p-8">
          <h1 className="font-display font-extrabold text-2xl mb-1">Min prestation</h1>
          <p className="text-sm text-zinc-500 mb-6">Hur du presterar i chatten – uppdateras löpande.</p>
          {!stats ? <CircleNotch size={24} className="animate-spin text-[#0033FF]" /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl" data-testid="agent-stats">
              <StatPill icon={Tray} label="I kö nu" value={stats.queue} />
              <StatPill icon={ChatCircleDots} label="Aktiva (mina)" value={stats.active_mine} />
              <StatPill icon={CheckCircle} label="Lösta av mig" value={stats.resolved_mine} />
              <StatPill icon={Headset} label="Hanterade totalt" value={stats.handled_mine} />
              <StatPill icon={Clock} label="Snittsvarstid" value={stats.avg_response_seconds != null ? `${stats.avg_response_seconds}s` : "—"} />
              <StatPill icon={Smiley} label="Nöjdhet (CSAT)" value={stats.csat != null ? `${stats.csat}%` : "—"} />
            </div>
          )}
        </div>
      ) : tab === "insights" ? (
        <div className="flex-1 overflow-y-auto p-8" data-testid="chat-insights">
          <div className="flex items-start justify-between gap-4 mb-6 max-w-5xl">
            <div>
              <h1 className="font-display font-extrabold text-2xl mb-1 flex items-center gap-2"><Sparkle size={24} weight="fill" className="text-[#0033FF]" /> AI-insikter · Chattar</h1>
              <p className="text-sm text-zinc-500">AI analyserar alla chattkonversationer och lyfter teman, vanliga frågor och förbättringsförslag.{insights && insights.generated_at ? ` Senast uppdaterad ${timeAgo(insights.generated_at)}.` : ""}</p>
            </div>
            <button data-testid="generate-insights" onClick={generateInsights} disabled={insGenerating}
              className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-[#0033FF] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#002BE6] disabled:opacity-60">
              {insGenerating ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} {insights && insights.summary ? "Uppdatera" : "Generera insikter"}
            </button>
          </div>

          {insights === null ? (
            <CircleNotch size={24} className="animate-spin text-[#0033FF]" />
          ) : !insights || !insights.summary ? (
            <div className="bg-white border border-zinc-200 rounded-xl py-16 text-center max-w-5xl" data-testid="insights-empty">
              <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center"><Sparkle size={24} weight="duotone" className="text-[#0033FF]" /></div>
              <h3 className="mt-4 font-display font-bold text-lg">Inga insikter än</h3>
              <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Klicka på "Generera insikter" så analyserar AI alla chattar och visar teman, vanliga frågor och rekommendationer.</p>
            </div>
          ) : (
            <div className="space-y-6 max-w-5xl">
              <div className="bg-white border border-zinc-200 rounded-xl p-6">
                <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 mb-2">Sammanfattning</div>
                <p className="text-zinc-800 leading-relaxed">{insights.summary}</p>
                <p className="mt-3 text-xs text-zinc-400">Baserat på {insights.analyzed_messages || 0} kundmeddelanden i {insights.analyzed_chats || 0} chattar.</p>
              </div>

              {(insights.themes || []).length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="insights-themes">
                  <h2 className="text-sm font-bold text-zinc-700 mb-4">Framväxande teman</h2>
                  <div className="space-y-3">
                    {insights.themes.map((t, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <span className="shrink-0 mt-0.5 text-[10px] font-bold uppercase rounded px-1.5 py-0.5" style={sevStyle(t.severity)}>{sevLabel(t.severity)}</span>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm flex items-center gap-2">{t.title}<span className="text-xs font-normal text-zinc-400">· {t.count}</span></div>
                          {t.example && <div className="text-xs text-zinc-500 italic mt-0.5">"{t.example}"</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {(insights.top_questions || []).length > 0 && (
                  <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="insights-questions">
                    <h2 className="text-sm font-bold text-zinc-700 mb-4 flex items-center gap-1.5"><ChatCircleDots size={16} weight="fill" className="text-[#0033FF]" /> Vanligaste frågorna</h2>
                    <div className="space-y-2">
                      {insights.top_questions.map((q, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-zinc-700 truncate">{q.question}</span>
                          <span className="shrink-0 text-xs font-bold text-zinc-400">{q.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(insights.categories || []).length > 0 && (
                  <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="insights-categories">
                    <h2 className="text-sm font-bold text-zinc-700 mb-4">Kategorier</h2>
                    <div className="space-y-2">
                      {insights.categories.map((c, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-sm">
                          <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: sentColor(c.sentiment) }} /> {c.name}</span>
                          <span className="text-xs font-bold text-zinc-400">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(insights.recommendations || []).length > 0 && (
                <div className="bg-gradient-to-br from-[#0033FF]/5 to-violet-50 border border-[#0033FF]/15 rounded-xl p-6" data-testid="insights-recommendations">
                  <h2 className="text-sm font-bold text-zinc-800 mb-4 flex items-center gap-1.5"><Sparkle size={16} weight="fill" className="text-[#0033FF]" /> Rekommendationer</h2>
                  <ul className="space-y-2.5">
                    {insights.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-700">
                        <CheckCircle size={18} weight="fill" className="text-[#0033FF] shrink-0 mt-0.5" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex min-h-0">
          {/* conversation list */}
          <aside className="w-80 shrink-0 border-r border-zinc-200 bg-white flex flex-col">
            <div className="p-3 border-b border-zinc-100 flex gap-1">
              {[["queue", `Kö${counts.queue ? ` (${counts.queue})` : ""}`], ["mine", `Mina${counts.mine ? ` (${counts.mine})` : ""}`], ["all", "Alla"]].map(([k, lbl]) => (
                <button key={k} data-testid={`inbox-scope-${k}`} onClick={() => setScope(k)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${scope === k ? "bg-[#0033FF] text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* SLA: live breach banner + threshold settings */}
            <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between gap-2 relative">
              {counts.sla_breached > 0 ? (
                <span data-testid="sla-breach-banner" className="flex items-center gap-1.5 text-[11px] font-bold text-red-600 bg-red-50 rounded-md px-2 py-1 animate-pulse">
                  <Warning size={13} weight="fill" /> {counts.sla_breached} chatt{counts.sla_breached > 1 ? "ar" : ""} över SLA
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-zinc-400">
                  <Clock size={13} weight="fill" /> SLA-larm: {sla.breach_seconds}s
                </span>
              )}
              <button data-testid="sla-settings-toggle" onClick={() => setSlaOpen((o) => !o)}
                className="text-[11px] font-semibold text-[#0033FF] hover:underline">Ändra</button>
              {slaOpen && (
                <SlaEditor sla={sla} onClose={() => setSlaOpen(false)} onSave={(w, b) => { saveSla(w, b); setSlaOpen(false); }} />
              )}
            </div>
            <div className="flex-1 overflow-y-auto scroll-thin" data-testid="conv-list">
              {list.length === 0 && (
                <div className="p-8 text-center text-sm text-zinc-400">
                  <Tray size={28} className="mx-auto mb-2 opacity-40" />
                  {scope === "queue" ? "Inga väntande chattar 🎉" : "Inget här ännu."}
                </div>
              )}
              {list.map((c) => {
                const st = STATUS_META[c.status] || STATUS_META.ai;
                return (
                  <button key={c.id} data-testid={`conv-item-${c.id}`} onClick={() => openConv(c.id)}
                    className={`w-full text-left px-3 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors ${activeId === c.id ? "bg-blue-50/60" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate flex items-center gap-1.5">
                        <ChannelIcon ch={c.channel} /> {c.agent_name || "Chatt"}
                      </span>
                      <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-xs text-zinc-500 truncate mt-1">{c.last_customer_text || c.last_message || "—"}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      {c.status === "waiting" ? (() => {
                        const w = waitSeconds(c.handover_at);
                        const breach = w != null && w >= sla.breach_seconds;
                        const warn = w != null && w >= sla.warn_seconds;
                        const cls = breach ? "bg-red-100 text-red-700 animate-pulse" : warn ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                        return (
                          <span data-testid={`sla-timer-${c.id}`} className={`text-[10px] font-bold rounded px-1.5 py-0.5 inline-flex items-center gap-1 ${cls}`}>
                            {breach && <Warning size={10} weight="fill" />}<Clock size={10} weight="fill" /> {fmtClock(w)}
                          </span>
                        );
                      })() : (
                        <span className="text-[10px] text-zinc-400">{timeAgo(c.handover_at || c.updated_at)}</span>
                      )}
                      <div className="flex items-center gap-1.5">
                        {c.notes_count > 0 && <span className="text-[10px] text-zinc-400 inline-flex items-center gap-0.5"><NotePencil size={10} weight="fill" /> {c.notes_count}</span>}
                        {c.agent_unread > 0 && <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5">{c.agent_unread}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* conversation thread */}
          <main className="flex-1 flex flex-col min-w-0 bg-[#FAFAFA]">
            {!activeId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
                <ChatCircleDots size={40} className="opacity-30 mb-3" />
                <p className="text-sm">Välj en konversation för att börja svara.</p>
              </div>
            ) : !conv ? (
              <div className="flex-1 flex items-center justify-center"><CircleNotch size={26} className="animate-spin text-[#0033FF]" /></div>
            ) : (
              <>
                {/* thread header */}
                <div className="shrink-0 bg-white border-b border-zinc-200 px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold flex items-center gap-2">{conv.agent_name || "Konversation"}
                      <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${(STATUS_META[conv.status] || STATUS_META.ai).cls}`}>{(STATUS_META[conv.status] || STATUS_META.ai).label}</span>
                    </div>
                    <div className="text-xs text-zinc-400">{conv.message_count} meddelanden · {conv.channel === "voice" ? "Röst" : "Chatt"}{conv.assigned_agent_name ? ` · ${conv.assigned_agent_name}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.status !== "active" && conv.status !== "resolved" && (
                      <button data-testid="btn-claim" onClick={() => act("claim")} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0033FF] text-white hover:bg-[#002BE6]">Ta över</button>
                    )}
                    {conv.status === "active" && (
                      <button data-testid="btn-release" onClick={() => act("release")} className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1.5"><Robot size={15} weight="fill" /> Till AI</button>
                    )}
                    {conv.status !== "resolved" && (
                      <button data-testid="btn-transfer" onClick={openTransfer} className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1.5"><ArrowsLeftRight size={15} weight="fill" /> Överlämna</button>
                    )}
                    {conv.status !== "resolved" && (
                      <button data-testid="btn-resolve" onClick={() => act("resolve")} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"><CheckCircle size={15} weight="fill" /> Lös</button>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex min-h-0">
                  {/* messages */}
                  <div ref={threadRef} className="flex-1 overflow-y-auto scroll-thin p-5 space-y-3" data-testid="conv-thread">
                    {(conv.messages || []).map((m, i) => {
                      const isUser = m.role === "user";
                      const isAgent = m.role === "agent";
                      return (
                        <div key={i} className={`flex gap-2 ${isUser ? "justify-start" : "justify-end"}`}>
                          {isUser && <div className="h-7 w-7 rounded-full bg-zinc-200 flex items-center justify-center shrink-0 text-zinc-500"><UserCircle size={16} weight="fill" /></div>}
                          <div className={`max-w-[72%] text-sm px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap break-words ${
                            isUser ? "bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm"
                              : isAgent ? "bg-[#0033FF] text-white rounded-br-sm"
                              : "bg-zinc-100 text-zinc-600 rounded-br-sm border border-zinc-200"}`}>
                            {!isUser && <div className="text-[10px] font-bold uppercase opacity-70 mb-0.5 flex items-center gap-1">{isAgent ? <><Headset size={10} weight="fill" /> {m.agent_name || "Du"}</> : <><Robot size={10} weight="fill" /> AI</>}</div>}
                            {m.content}
                          </div>
                        </div>
                      );
                    })}
                    {customerTyping && (
                      <div className="flex gap-2 justify-start" data-testid="customer-typing">
                        <div className="h-7 w-7 rounded-full bg-zinc-200 flex items-center justify-center shrink-0 text-zinc-500"><UserCircle size={16} weight="fill" /></div>
                        <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-sm px-3.5 py-3 flex items-center gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI insights */}
                  <aside className="w-64 shrink-0 border-l border-zinc-200 bg-white flex flex-col min-h-0" data-testid="ai-insights">
                    <div className="p-4 space-y-4 shrink-0">
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 mb-2">AI-insikter</div>
                        {(() => {
                          const s = SENT[conv.sentiment] || SENT.neutral;
                          return (
                            <div className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2.5" data-testid="insight-sentiment">
                              <s.Icon size={22} weight="fill" style={{ color: s.color }} />
                              <div>
                                <div className="text-xs text-zinc-400">Kundens humör</div>
                                <div className="text-sm font-bold" style={{ color: s.color }}>{s.label}</div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-xs text-zinc-500 space-y-1.5">
                        <div className="flex justify-between"><span>Kanal</span><span className="font-semibold text-zinc-700">{conv.channel === "voice" ? "Röst" : "Chatt"}</span></div>
                        <div className="flex justify-between"><span>Meddelanden</span><span className="font-semibold text-zinc-700">{conv.message_count}</span></div>
                        <div className="flex justify-between"><span>Överlämnad</span><span className="font-semibold text-zinc-700">{timeAgo(conv.handover_at) || "—"}</span></div>
                      </div>
                      <button data-testid="btn-suggest" onClick={suggest} disabled={suggesting}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-violet-600 text-white text-sm font-semibold py-2.5 hover:bg-violet-700 disabled:opacity-60">
                        {suggesting ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} AI-förslag på svar
                      </button>
                    </div>

                    {/* internal KB assistant — answers grounded ONLY in this bot's knowledge base */}
                    <div className="flex-1 flex flex-col min-h-0 border-t border-zinc-100 p-4 pt-3" data-testid="kb-assistant">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 mb-1 flex items-center gap-1.5 shrink-0"><ChatCircleDots size={13} weight="fill" className="text-violet-600" /> Fråga kunskapsbasen</div>
                      <p className="text-[11px] text-zinc-400 mb-2 leading-relaxed shrink-0">Fråga botens kunskapsbas om du behöver veta något – kunden ser aldrig detta.</p>
                      <div ref={kbThreadRef} className="flex-1 min-h-0 rounded-lg border border-zinc-200 bg-zinc-50/60 p-2 space-y-2 overflow-y-auto scroll-thin mb-2" data-testid="kb-thread">
                        {kbMsgs.length === 0 && !kbLoading && (
                          <p className="text-[11px] text-zinc-400 text-center py-3">Ställ en fråga, t.ex. "Vad är returpolicyn?"</p>
                        )}
                        {kbMsgs.map((m, i) => (
                          <div key={i} className={`text-xs ${m.role === "user" ? "text-right" : "text-left"}`}>
                            <div className={`inline-block max-w-[92%] px-2.5 py-1.5 rounded-xl whitespace-pre-wrap break-words ${
                              m.role === "user" ? "bg-[#0033FF] text-white rounded-br-sm"
                                : m.error ? "bg-red-50 text-red-600 border border-red-100 rounded-bl-sm"
                                : "bg-white text-zinc-700 border border-zinc-200 rounded-bl-sm"}`} data-testid={`kb-msg-${i}`}>
                              {m.content}
                              {m.role === "assistant" && !m.error && (
                                <button data-testid={`kb-use-${i}`} onClick={() => applyKbAsReply(m.content)}
                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0033FF] hover:underline">
                                  <PaperPlaneTilt size={11} weight="fill" /> Använd som svar
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {kbLoading && <div className="text-left"><span className="inline-flex items-center gap-1.5 text-xs text-zinc-400 bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5"><CircleNotch size={12} className="animate-spin" /> Söker i kunskapsbasen…</span></div>}
                      </div>
                      <div className="flex items-end gap-1.5 shrink-0">
                        <textarea data-testid="kb-input" value={kbInput} onChange={(e) => setKbInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendKb(); } }}
                          rows={1} placeholder="Fråga kunskapsbasen…"
                          className="flex-1 resize-none rounded-lg border border-zinc-200 px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400/30 max-h-24" />
                        <button data-testid="kb-send" onClick={sendKb} disabled={kbLoading || !kbInput.trim()}
                          className="h-9 w-9 rounded-lg bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:opacity-50 shrink-0">
                          {kbLoading ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="fill" />}
                        </button>
                      </div>
                    </div>
                  </aside>
                </div>

                {/* composer */}
                <div className="shrink-0 bg-white border-t border-zinc-200 p-3">
                  <div className="flex items-center gap-2 mb-2 relative">
                    <button data-testid="btn-canned" onClick={() => setCannedOpen((o) => !o)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-semibold px-2.5 py-1.5">
                      <Lightning size={14} weight="fill" className="text-[#0033FF]" /> Snabbsvar
                    </button>
                    {cannedOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setCannedOpen(false)} />
                        <div className="absolute bottom-full mb-2 left-0 z-20 w-80 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-zinc-200 p-1.5" data-testid="canned-menu">
                          <div className="flex items-center justify-between px-2 py-1.5">
                            <span className="text-[11px] font-bold uppercase text-zinc-400">Snabbsvar</span>
                            <button data-testid="manage-canned" onClick={() => { setCannedOpen(false); setManageCanned(true); }} className="text-[11px] font-semibold text-[#0033FF] hover:underline flex items-center gap-1"><PencilSimple size={11} /> Hantera</button>
                          </div>
                          {canned.length === 0 && <p className="px-2 py-3 text-xs text-zinc-400 text-center">Inga snabbsvar ännu. Klicka på "Hantera" för att skapa.</p>}
                          {canned.map((c) => (
                            <button key={c.id} data-testid={`canned-item-${c.id}`} onClick={() => insertCanned(c.content)}
                              className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-zinc-50">
                              <div className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5">{c.title}{c.shortcut && <span className="text-[10px] font-mono text-zinc-400">{c.shortcut}</span>}</div>
                              <div className="text-[11px] text-zinc-500 truncate">{c.content}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <textarea
                      data-testid="composer-input"
                      value={draft}
                      onChange={(e) => onDraftChange(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      rows={1}
                      placeholder={conv.status === "resolved" ? "Konversationen är löst – skriv för att återöppna." : "Skriv ditt svar… (Enter för att skicka)"}
                      className="flex-1 resize-none rounded-xl border border-zinc-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30 max-h-32"
                    />
                    <button data-testid="composer-send" onClick={sendReply} disabled={sending || !draft.trim()}
                      className="h-11 w-11 rounded-xl bg-[#0033FF] text-white flex items-center justify-center hover:bg-[#002BE6] disabled:opacity-50 shrink-0">
                      {sending ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      )}

      {/* transfer to colleague modal */}
      {transferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="transfer-modal" onClick={() => setTransferOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><ArrowsLeftRight size={20} weight="fill" className="text-[#0033FF]" /> Överlämna chatt</h3>
              <button onClick={() => setTransferOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Välj en kollega att lämna över konversationen till.</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {colleagues.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">Inga andra agenter är online just nu.</p>}
              {colleagues.map((a) => (
                <button key={a.id} data-testid={`transfer-agent-${a.id}`} onClick={() => doTransfer(a.id)} disabled={a.load >= a.max_chats}
                  className="w-full flex items-center justify-between rounded-xl border border-zinc-200 px-3.5 py-3 hover:bg-zinc-50 disabled:opacity-50 text-left">
                  <div className="flex items-center gap-2.5">
                    <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: AVAIL[a.status]?.color || "#10b981" }} />
                    <div>
                      <div className="font-semibold text-sm">{a.name}</div>
                      <div className="text-[11px] text-zinc-400">{AVAIL[a.status]?.label || "Online"} · {a.load}/{a.max_chats} chattar</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#0033FF]">{a.load >= a.max_chats ? "Full" : "Välj"}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* manage canned responses modal */}
      {manageCanned && (
        <ManageCannedModal canned={canned} onClose={() => setManageCanned(false)} onChanged={loadCanned} />
      )}
    </div>
  );
}

function SlaEditor({ sla, onClose, onSave }) {
  const [warn, setWarn] = useState(sla.warn_seconds);
  const [breach, setBreach] = useState(sla.breach_seconds);
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute top-full right-3 mt-1 z-20 w-60 bg-white rounded-xl shadow-xl border border-zinc-200 p-3" data-testid="sla-editor">
        <div className="text-[11px] font-bold uppercase text-zinc-400 mb-2">SLA-trösklar (sekunder)</div>
        <label className="block text-xs text-zinc-500 mb-1">Varning efter</label>
        <input data-testid="sla-warn" type="number" value={warn} onChange={(e) => setWarn(parseInt(e.target.value || "0", 10))}
          className="w-full rounded-lg border border-zinc-200 px-2.5 py-1.5 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-amber-400/30" />
        <label className="block text-xs text-zinc-500 mb-1">Larm (överskriden) efter</label>
        <input data-testid="sla-breach" type="number" value={breach} onChange={(e) => setBreach(parseInt(e.target.value || "0", 10))}
          className="w-full rounded-lg border border-zinc-200 px-2.5 py-1.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-400/30" />
        <button data-testid="sla-save" onClick={() => onSave(warn, breach)} className="w-full rounded-lg bg-[#0033FF] text-white text-sm font-semibold py-2 hover:bg-[#002BE6]">Spara</button>
      </div>
    </>
  );
}

function ManageCannedModal({ canned, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // {id?, title, content, shortcut}
  const blank = { title: "", content: "", shortcut: "" };

  const save = async () => {
    if (!editing.title.trim() || !editing.content.trim()) { toast.error("Titel och text krävs"); return; }
    try {
      if (editing.id) await api.put(`/inbox/canned/${editing.id}`, editing);
      else await api.post("/inbox/canned", editing);
      toast.success("Snabbsvar sparat");
      setEditing(null);
      await onChanged();
    } catch { toast.error("Kunde inte spara"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/inbox/canned/${id}`); await onChanged(); toast.success("Borttaget"); }
    catch { toast.error("Kunde inte ta bort"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="manage-canned-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><Lightning size={20} weight="fill" className="text-[#0033FF]" /> Hantera snabbsvar</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
        </div>

        {editing ? (
          <div className="space-y-3" data-testid="canned-form">
            <div>
              <label className="block text-xs font-semibold text-zinc-500 mb-1">Titel</label>
              <input data-testid="canned-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30" placeholder="t.ex. Hälsning" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-500 mb-1">Genväg (valfritt)</label>
              <input data-testid="canned-shortcut" value={editing.shortcut} onChange={(e) => setEditing({ ...editing, shortcut: e.target.value })}
                className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30" placeholder="t.ex. /hej" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-500 mb-1">Text</label>
              <textarea data-testid="canned-content" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                rows={4} className="w-full resize-none rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30" placeholder="Skriv svaret…" />
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="canned-save" onClick={save} className="flex-1 rounded-lg bg-[#0033FF] text-white text-sm font-semibold py-2.5 hover:bg-[#002BE6]">Spara</button>
              <button onClick={() => setEditing(null)} className="rounded-lg border border-zinc-200 text-zinc-600 text-sm font-semibold px-4 py-2.5 hover:bg-zinc-50">Avbryt</button>
            </div>
          </div>
        ) : (
          <>
            <button data-testid="canned-new" onClick={() => setEditing({ ...blank })}
              className="w-full mb-3 flex items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-zinc-200 text-zinc-500 hover:border-[#0033FF] hover:text-[#0033FF] text-sm font-semibold py-2.5">
              <Plus size={16} weight="bold" /> Nytt snabbsvar
            </button>
            <div className="space-y-2">
              {canned.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">Inga snabbsvar ännu.</p>}
              {canned.map((c) => (
                <div key={c.id} className="rounded-xl border border-zinc-200 p-3 flex items-start justify-between gap-2" data-testid={`canned-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5">{c.title}{c.shortcut && <span className="text-[10px] font-mono text-zinc-400">{c.shortcut}</span>}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{c.content}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`canned-edit-${c.id}`} onClick={() => setEditing({ id: c.id, title: c.title, content: c.content, shortcut: c.shortcut || "" })}
                      className="h-8 w-8 rounded-lg text-zinc-500 hover:bg-zinc-100 flex items-center justify-center"><PencilSimple size={15} /></button>
                    <button data-testid={`canned-delete-${c.id}`} onClick={() => remove(c.id)}
                      className="h-8 w-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
